/**
 * CreditPurchaseModal - Modal for purchasing credit packs
 */

import { useState } from 'react';
import { X, Coins, Check, Loader2, ExternalLink, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CreditPack } from '@/hooks/useCredits';

interface CreditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  packs: CreditPack[];
  currentBalance: number;
  onPurchase: (packId: string) => Promise<string | null>;
}

export function CreditPurchaseModal({
  isOpen,
  onClose,
  packs,
  currentBalance,
  onPurchase,
}: CreditPurchaseModalProps) {
  const [selectedPack, setSelectedPack] = useState<string | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handlePurchase = async () => {
    if (!selectedPack) return;

    setIsPurchasing(true);
    setError(null);

    try {
      const checkoutUrl = await onPurchase(selectedPack);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };

  const sortedPacks = [...packs].sort((a, b) => a.price_usd - b.price_usd);
  const bestValuePack = packs.reduce((best, pack) =>
    pack.bonus_percent > best.bonus_percent ? pack : best, packs[0]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-lg bg-surface border border-border-subtle rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-verdict-primary/20 rounded-lg">
              <Coins className="w-5 h-5 text-verdict-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Buy Credits</h2>
              <p className="text-sm text-text-secondary">
                Current balance: <span className="text-text-primary font-medium">{currentBalance.toLocaleString()}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-light transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Packs */}
        <div className="p-6 space-y-3">
          {sortedPacks.map((pack) => {
            const isBestValue = pack.id === bestValuePack?.id && pack.bonus_percent > 0;
            return (
              <button
                key={pack.id}
                onClick={() => setSelectedPack(pack.id)}
                className={cn(
                  'w-full flex items-center justify-between p-4 rounded-xl border transition-all relative',
                  selectedPack === pack.id
                    ? 'bg-verdict-primary/10 border-verdict-primary/50'
                    : 'bg-surface-light border-border-subtle hover:border-border-light',
                  isBestValue && 'ring-1 ring-green-500/50'
                )}
              >
                {isBestValue && (
                  <div className="absolute -top-2 left-4 px-2 py-0.5 bg-green-500 text-black text-xs font-bold rounded">
                    BEST VALUE
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <div className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    selectedPack === pack.id ? 'bg-verdict-primary/20' : 'bg-surface-light'
                  )}>
                    {selectedPack === pack.id ? (
                      <Check className="w-5 h-5 text-verdict-primary" />
                    ) : (
                      <Coins className="w-5 h-5 text-text-secondary" />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-text-primary">{pack.name}</span>
                      {pack.bonus_percent > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-500/20 text-green-400 rounded-full flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          +{pack.bonus_percent}%
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-secondary">{pack.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-text-primary">${pack.price_usd}</p>
                  <p className="text-sm text-verdict-primary">{pack.credits.toLocaleString()} credits</p>
                </div>
              </button>
            );
          })}
        </div>

        {error && (
          <div className="px-6 pb-2">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 border-t border-border-subtle">
          <button
            onClick={handlePurchase}
            disabled={!selectedPack || isPurchasing}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all',
              selectedPack && !isPurchasing
                ? 'bg-verdict-primary text-black hover:bg-verdict-primary/90'
                : 'bg-surface-light text-text-muted cursor-not-allowed'
            )}
          >
            {isPurchasing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Redirecting to checkout...</span>
              </>
            ) : (
              <>
                <ExternalLink className="w-5 h-5" />
                <span>Continue to Checkout</span>
              </>
            )}
          </button>
          <p className="text-xs text-text-muted text-center mt-3">
            Secure payment powered by Stripe
          </p>
        </div>
      </div>
    </div>
  );
}
